/* ============================================================================
 * preorder.js — Pedal Industries native preorder helper.
 * Phase 3 (PRE-08): vanilla-JS mirror of the Liquid activation rule in
 *   snippets/preorder-buy-button-state.liquid. Loaded by layout/theme.liquid
 *   on product templates only. Zero external deps, < 5KB.
 * Phase 4 (PRE-05): document-level variant:change listener live-updates
 *   the wrapper data-preorder attr + button label + disabled state.
 * Analytics: ZERO calls here. Future events MUST route through Elevar
 *   (project memory feedback_pedal_elevar_tracking — no direct Google/Meta
 *   tag calls or direct dataLayer pushes on Pedal).
 * ============================================================================
 */
(function () {
  'use strict';

  window.PedalPreorder = {
    /**
     * Authoritative server-side preorder state keyed by variant id.
     * Populated from <script type="application/json" data-pedal-preorder-map>
     * blobs that each buy-button surface renders in Liquid (where
     * inventory_quantity + inventory_policy are always available).
     *
     * This exists because the variant objects delivered by Motion's legacy
     * variant-change events come from `{{ product | json }}`, which OMITS
     * inventory_policy and inventory_quantity — so isPreorderVariant() can't
     * judge them and would wrongly disable the PN-Note property on every
     * swatch change (custom-name / ISD bag forms). The map removes that
     * dependency. Mirrors the per-section map used by product-2025.js.
     */
    preorderByVariantId: {},

    /** Merge a {variantId: bool} map into the authoritative store. */
    registerPreorderMap: function (map) {
      if (!map) return;
      for (var k in map) {
        if (Object.prototype.hasOwnProperty.call(map, k)) {
          this.preorderByVariantId[String(k)] = Boolean(map[k]);
        }
      }
    },

    /** Scan the DOM for data-pedal-preorder-map blobs and merge them in. */
    collectPreorderMaps: function () {
      var nodes = document.querySelectorAll('[data-pedal-preorder-map]');
      for (var i = 0; i < nodes.length; i++) {
        if (nodes[i]._pedalParsed) continue;
        try {
          this.registerPreorderMap(JSON.parse(nodes[i].textContent || '{}'));
          nodes[i]._pedalParsed = true;
        } catch (e) { /* ignore malformed blob */ }
      }
    },

    /**
     * Resolve preorder state for a variant. Prefers the authoritative
     * server-side map (by id); only falls back to the field-based rule when
     * the variant id is absent from every rendered map.
     */
    resolveIsPreorder: function (variant) {
      if (!variant) return false;
      var id = variant.id != null ? String(variant.id) : null;
      if (id && Object.prototype.hasOwnProperty.call(this.preorderByVariantId, id)) {
        return this.preorderByVariantId[id];
      }
      return this.isPreorderVariant(variant);
    },

    /**
     * Mirror of the Liquid activation rule.
     * Pass a Shopify storefront variant JSON object (e.g. from
     * product.variants[i] or product.selected_or_first_available_variant | json).
     *
     * Returns true iff:
     *   variant.inventory_management is truthy AND
     *   variant.inventory_policy === 'continue' AND
     *   variant.inventory_quantity is null/undefined OR <= 0
     *
     * @param {object} variant - Shopify storefront variant JSON
     * @returns {boolean}
     */
    isPreorderVariant: function (variant) {
      if (!variant) return false;
      if (!variant.inventory_management) return false;
      if (variant.inventory_policy !== 'continue') return false;
      var qty = variant.inventory_quantity;
      if (qty == null) return true;
      return qty <= 0;
    },

    /**
     * Returns the button label string for a given variant, matching the Liquid
     * decision tree in snippets/preorder-buy-button-state.liquid exactly.
     * Useful for Phase 4 variant-switcher updates.
     *
     * @param {object} variant - Shopify variant JSON
     * @param {object} [labels] - { default, soldOut, preorder } strings
     * @returns {string}
     */
    buttonLabelFor: function (variant, labels) {
      labels = labels || {};
      var fallback = {
        default: 'Add to cart',
        soldOut: 'Sold Out',
        preorder: 'Pre-order'
      };
      var L = {
        default: labels.default || fallback.default,
        soldOut: labels.soldOut || fallback.soldOut,
        preorder: labels.preorder || fallback.preorder
      };
      if (!variant) return L.default;
      if (!variant.inventory_management) return L.default;
      if (variant.available === false && variant.inventory_policy !== 'continue') {
        return L.soldOut;
      }
      if (this.isPreorderVariant(variant)) return L.preorder;
      return L.default;
    },

    /**
     * Apply variant state to a [data-preorder] wrapper. Labels are read
     * from the button's data-*-text attrs (Phase 3 emits translated values).
     */
    applyToWrapper: function (wrapperEl, variant) {
      if (!wrapperEl || !variant) return;
      var btn = wrapperEl.querySelector('[data-add-to-cart]');
      if (!btn) return;
      var labels = {
        default: btn.getAttribute('data-default-text') || 'Add to cart',
        preorder: btn.getAttribute('data-preorder-text') || 'Pre-order',
        soldOut: btn.getAttribute('data-soldout-text') || 'Sold Out'
      };
      var isPreorder = this.resolveIsPreorder(variant);
      var label = isPreorder ? labels.preorder : this.buttonLabelFor(variant, labels);
      wrapperEl.dataset.preorder = isPreorder ? 'true' : 'false';
      var span = btn.querySelector('[data-add-to-cart-text]');
      if (span) span.textContent = label;
      btn.setAttribute('aria-label', label);
      if (variant.available === false && variant.inventory_policy !== 'continue') {
        btn.setAttribute('disabled', 'disabled');
      } else {
        btn.removeAttribute('disabled');
      }
      var hiddenProp = wrapperEl.querySelector('[data-preorder-prop]');
      if (hiddenProp) {
        if (isPreorder) {
          hiddenProp.removeAttribute('disabled');
        } else {
          hiddenProp.setAttribute('disabled', 'disabled');
        }
      }
    },

    /**
     * Public API for per-form attach. Auto-init already covers every form via
     * a document-level listener, so this is a sentinel returning true.
     */
    attachToProductForm: function (formEl) { return !!formEl; },

    /** Semantic version of this helper, bumped when behavior changes. */
    version: '1.3.0'
  };

  function _initPedalPreorderVariantListener() {
    window.PedalPreorder.collectPreorderMaps();
    document.addEventListener('variant:change', function (e) {
      var variant = e && e.detail && e.detail.variant;
      if (!variant) return;
      // Re-scan in case a form (e.g. quickview modal) was injected after init.
      window.PedalPreorder.collectPreorderMaps();
      var wraps = document.querySelectorAll('[data-preorder]');
      for (var i = 0; i < wraps.length; i++) {
        window.PedalPreorder.applyToWrapper(wraps[i], variant);
      }
      var isPreorder = window.PedalPreorder.resolveIsPreorder(variant);
      var hiddens = document.querySelectorAll('[data-preorder-prop]');
      for (var j = 0; j < hiddens.length; j++) {
        if (isPreorder) {
          hiddens[j].removeAttribute('disabled');
        } else {
          hiddens[j].setAttribute('disabled', 'disabled');
        }
      }
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', _initPedalPreorderVariantListener);
  } else {
    _initPedalPreorderVariantListener();
  }
})();
